/**
 * @unscoped — 주소록의 i18n (migration 171, 2026-09-08).
 *
 * mail-i18n / approval-i18n 과 같은 모양이다.
 * 쓰는 곳: ContactsPage · ContactEditModal · PersonCard(F3-2) · ContactPickModal(E1-4) · MailDetail.
 *   (다섯이다. 2026-10-06 까지 넷만 적혀 있었다 —
 *    `git grep -l contacts-i18n -- apps/frontend/src | grep -v '\.test\.'` = 5)
 *
 * 정책:
 *   - fallback chain: lang → en → FALLBACK_LANG (ko)
 *
 * ## ⭐ 2026-10-01 사용자 결정 — 「명함첩」인데 왜 「연락처」가 남아 있나
 *
 * 2026-09-29 에 「주소록 → 명함첩」으로 21줄을 바꾸면서, 아래 다섯 자리에는 **「연락처」가
 * 그대로 남았다.** 「빠뜨린 것 아닌가」로 읽기 쉬워 갈래를 만들어 여쭈었고 답을 받았다:
 *
 * ```
 * ⒜ 그대로 둔다        ← ★ 사용자가 고른 것
 *    「명함첩」은 «책»이고, 그 안의 한 장은 여전히 «연락처»다. 뜻이 어긋나지 않는다
 * ⒝ 「명함 추가 / 명함 수정 / 명함 열기 / 이 명함을 지울까요?」로 맞춤   ⇒ 기각
 * ⒞ 단추만 「명함첩에 추가」, 나머지는 ⒜                                ⇒ 기각
 * ```
 *
 * ⇒ ⛔ **그러니 아래 다섯은 «고치지 않는다».** 「빠진 것」이 아니라 «그렇게 정한 것»이다:
 *    `contacts.add` · `contacts.edit.new` · `contacts.edit.edit` ·
 *    `contacts.card.open` · `contacts.delete.confirm`
 *
 * ⚠ 되돌아보는 잣대 — 「그릇(명함첩)」과 「알맹이(연락처)」를 가리키는 말이다. 그릇을 가리키는
 *   자리에 「연락처」가 나오면 그것은 이 결정과 «다른» 것이니 그때는 고친다.
 *   (「주소록」이 사람 눈에 되돌아오는 것은 `contacts-naming.guard.test.ts` 가 막는다.)
 *
 * ## ⏸ 2026-10-01 — 「명함」이 **두 뜻**이다. 지금은 정하지 않는다
 *
 * ```
 * ⓐ 명함첩 안의 «남의 한 장»              이 파일 · minutes-i18n · 대시보드 빠른 실행
 * ⓑ 메일 서명에 붙이는 «내 명함»(vCard)   mail-i18n 의 `mail.settings.vcard*` · `mail.vcard.*`
 *                                        (「명함 첨부」·「명함 관리」·「명함에 더할 항목」)
 * ```
 * ⒜ 결정 아래에서는 둘 다 말이 되지만, 같은 화면에서 만나면 헷갈릴 수 있다.
 * ⇒ **사용자 결정(2026-10-01): 지금 정하지 않는다** — 「전자명함」 판이 돌고 있어 **그 말이 정해진 뒤
 *   한 번에 본다.** ⛔ 그때까지 어느 쪽도 손대지 않는다.
 * ⇒ 그날이 오면 볼 자리: 위 ⓑ 의 키들과 `MailVcardModal` · `MailSettingsBasic`.
 *
 * ## ⭐ 2026-10-06 사용자 지시 — ⓐ «쪽만» 열렸다. ⓑ 는 여전히 보류
 *
 * 바로 위 ⏸ 가 **반만** 풀렸다. 사용자가 연 것은 ⓐ(명함첩 안의 남의 한 장) **하나뿐**이고,
 * ⓑ(메일 서명 vCard)는 **그대로 잠겨 있다.**
 *
 * ⛔⛔ **ⓐ 가 끝나는 것은 ⓑ 의 해제 조건이 «아니다».** 위 ⏸ 의 「그 말이 정해진 뒤 한 번에
 *     본다」만 읽으면 「ⓐ 가 났으니 이제 ⓑ 도 본다」로 읽히는데, 그 읽기가 틀렸다.
 *     ⓑ 를 열려면 아래가 **전부** 풀려야 한다 — 하나만 적어 두면 **그것이 ✅ 되는 날
 *     나머지가 함께 통과한다:**
 *
 * ```
 * ⓑ-1  사용자가 ⓑ 를 열라고 «말한 적이 없다»      ← 2026-10-06 지시는 ⓐ 뿐이었다
 * ⓑ-2  「내 명함(vCard)」을 무엇이라 부를지 아직 «안 정해졌다»
 * ⓑ-3  ⓐ 자체가 «안 끝났다» — 서버 칸이 없어 2026-10-06 현재 ⓐ 의 화면 코드는 0줄이다
 * ```
 * ⇒ ⓑ-3 이 가장 먼저 ✅ 가 될 텐데 **그것만으로는 아무것도 안 열린다.** 손대려면 ⓑ-1 이 있어야
 *   하고, ⛔ 관제·피어의 승인은 ⓑ-1 을 **대신하지 못한다**(사용자 관문이다).
 * ⇒ 손댈 자리는 종전과 같다: `mail.settings.vcard` 계열 · `mail.vcard` 계열 ·
 *   `MailVcardModal` · `MailSettingsBasic`.
 *
 * ## ⓐ 를 지을 때 볼 자리 (2026-10-06 실측 — 다시 재지 않아도 된다)
 *
 * 「사람 동그라미」를 그리는 자리가 셋이다. ⓐ 에 사진이 붙을 때 **넷째를 만들지 말 것:**
 * ```
 * 공용  components/shared/Avatar/Avatar.tsx   <img→이니셜 폴백 · 결정색 · 두 글자 · 34곳이 쓴다
 *       ContactsPage.tsx:163  (.ctp-name__av) 손으로 깎은 «한 글자 + ?»  ← 명함첩(외부 주소)
 *       PersonCard.tsx:164                    손으로 깎은 «한 글자 + ?»  ← HR 직원(우리 사용자)
 * ```
 * ⚠ **뒤의 둘은 «두 벌»이 아니다**(2026-10-06 관제가 전수로 바로잡음). 꼴만 같고 «대상»이 다르다 —
 *   `PersonCard` 는 `fetchPersonCard(userId)` 로 `GET /hr/people/:userId` 를 부르는 **직원** 카드다.
 *   ⛔ 모으면 틀린다: 직원은 `userId` 로 아바타를 찾고 명함은 `photoFileId` 로 찾는다.
 * ⛔ 그리고 공용 `<Avatar>` 도 **그대로는 못 쓴다** — `userId: number` 를 받는데
 *    `Contact`(api/contacts.ts)에는 userId 가 없다. 명함첩의 한 장은 «우리 사용자»가 아니라
 *    외부 주소라, 서버 `UserEcard`(chat-server ecard.service)와 **이어져 있지 않다.**
 *    ⇒ 2026-10-06 사용자·백엔드 결정도 그쪽이다: **`Contact` 에 칸을 더한다**
 *      (`contacts.photo_file_id` · 마이그 224). `UserEcard` 와 잇는 열쇠는 **안 쓴다.**
 *
 * ⛔⛔ **정정 — 2026-10-06 에 내가 여기 적었던 「`avatar-src.ts` 의 센티널 해석을 빌리라」는 틀렸다.**
 *     `avatarImageUrl` 은 `wcavatar:{ts}`/`wcfile:{id}`/`http` 만 알아듣고 **맨 파일 번호에는
 *     `null` 을 돌려준다.** `photoFileId` 는 맨 파일 번호이므로 그 길로 가면 사진이 영영 안 뜬다.
 *     ⇒ ★ 빌릴 꼴은 **`BoardsPage/wide/BoardThumb.tsx`** 다(목록 썸네일 — 이 판과 같은 자리):
 *       `requestPresignedDownload(fileId, { purpose: 'preview' })` → `<img loading="lazy">`,
 *       주소가 오기 전·실패한 뒤에는 자리를 지킨 채 폴백. `MailAttachThumb` · `ChatMediaPanel` 도 같다.
 *     ⇒ 지은 자리는 `pages/ContactsPage/ContactPhoto.tsx` **한 벌**이고 목록·수정 창이 함께 쓴다.
 * ⇒ 프론트에 「전자명함」을 그리는 자리는 **0건**이다(`git grep -lw 'ecard|Ecard|eCard'
 *   origin/main -- apps/frontend/src`). 대조군으로 같은 거르개를 chat-server 에 돌리면 **8 파일**이
 *   나오니, 거르개가 고장나서 0 이 된 것이 아니다.
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // ----- 화면 머리 · 좌측 목록 -----
  'contacts.title': { ko: '명함첩', en: 'Contacts', ja: 'アドレス帳' },
  'contacts.side.aria': { ko: '명함첩 목록', en: 'Contact lists', ja: 'アドレス帳一覧' },
  'contacts.add': { ko: '연락처 추가', en: 'Add contact', ja: '連絡先を追加' },
  'contacts.list.starred': { ko: '중요 명함첩', en: 'Starred', ja: 'お気に入り' },
  'contacts.list.personal': { ko: '개인 명함첩', en: 'My contacts', ja: '個人アドレス帳' },
  'contacts.list.shared': { ko: '공유 명함첩', en: 'Shared contacts', ja: '共有アドレス帳' },
  'contacts.group.tags': { ko: '태그', en: 'Tags', ja: 'タグ' },
  'contacts.blocked.link': { ko: '차단한 주소', en: 'Blocked addresses', ja: '受信拒否アドレス' },
  'contacts.desc.personal': {
    ko: '내가 담은 사람입니다. 회사 사람이 함께 보는 것은 「공유 명함첩」입니다.',
    en: 'People you saved. Everyone in the company shares the “Shared contacts” list.',
    ja: '自分が保存した連絡先です。会社で共有するものは「共有アドレス帳」です。',
  },
  'contacts.desc.shared': {
    ko: '회사가 함께 보는 명단입니다. 넣고 고치는 것은 관리자만 할 수 있습니다.',
    en: 'The company-wide list. Only admins can add or edit.',
    ja: '会社全体で見る名簿です。追加・修正は管理者のみです。',
  },
  'contacts.desc.starred': {
    ko: '별을 단 사람입니다. 개인·공유 어디에 있든 여기 모입니다.',
    en: 'Everyone you starred — from both your own and the shared list.',
    ja: 'スターを付けた連絡先です。個人・共有のどちらからも集まります。',
  },

  // ----- 표 -----
  'contacts.col.name': { ko: '이름', en: 'Name', ja: '名前' },
  'contacts.col.email': { ko: '이메일', en: 'Email', ja: 'メール' },
  'contacts.col.phone': { ko: '전화번호', en: 'Phone', ja: '電話番号' },
  'contacts.col.mobile': { ko: '휴대전화', en: 'Mobile', ja: '携帯電話' },
  'contacts.col.company': { ko: '회사', en: 'Company', ja: '会社' },
  'contacts.col.tags': { ko: '태그', en: 'Tags', ja: 'タグ' },
  'contacts.search.ph': {
    ko: '이름 · 이메일 · 회사 · 전화번호로 찾기',
    en: 'Search name, email, company or phone',
    ja: '名前・メール・会社・電話番号で検索',
  },
  'contacts.tag.all': { ko: '태그 전체', en: 'All tags', ja: 'すべてのタグ' },
  'contacts.sort.name': { ko: '이름순', en: 'By name', ja: '名前順' },
  'contacts.sort.email': { ko: '이메일순', en: 'By email', ja: 'メール順' },
  'contacts.sort.recent': { ko: '최근 담은 순', en: 'Recently added', ja: '追加順' },

  /**
   * ★ 「(이름없음)」은 빈칸이 아니라 **값**이다 — 주소만 아는 사람이 가장 많다.
   *   빈 칸으로 두면 정렬에서 사라지고 「내가 안 적은 것」과 구분되지 않는다.
   */
  'contacts.noname': { ko: '(이름없음)', en: '(No name)', ja: '(名前なし)' },

  // ----- 빈 화면 -----
  'contacts.empty.title': { ko: '아직 담은 사람이 없습니다', en: 'No contacts yet', ja: 'まだ連絡先がありません' },
  'contacts.empty.desc': {
    ko: '메일에서 보낸사람 이름을 눌러 담거나, 여기서 직접 넣을 수 있습니다.',
    en: 'Save someone from a mail by clicking their name, or add one here.',
    ja: 'メールの差出人名から保存するか、ここで直接追加できます。',
  },
  'contacts.empty.search': { ko: '찾는 사람이 없습니다', en: 'No matches', ja: '該当する連絡先がありません' },

  // ----- 편집 -----
  'contacts.edit.new': { ko: '연락처 추가', en: 'Add contact', ja: '連絡先を追加' },
  'contacts.edit.edit': { ko: '연락처 수정', en: 'Edit contact', ja: '連絡先を修正' },
  'contacts.edit.department': { ko: '부서 · 직위', en: 'Team · Title', ja: '部署・役職' },
  // 2026-10-06 지시 5·6 — 보이기 ＋ 바꾸기. ★ 말은 사용자 원문 그대로다:
  // 「연락처 수정에도 «명함 이미지»를 보이도록」 · 「명함의 «이미지를 수정»할 수 있도록」.
  'contacts.edit.photo': { ko: '명함 이미지', en: 'Card image', ja: '名刺画像' },
  'contacts.edit.photo_pick': { ko: '이미지 고르기', en: 'Choose image', ja: '画像を選ぶ' },
  'contacts.edit.photo_change': { ko: '이미지 바꾸기', en: 'Change image', ja: '画像を変える' },
  'contacts.edit.photo_remove': { ko: '이미지 떼기', en: 'Remove image', ja: '画像を外す' },
  // ⚠ 넷을 글자로 적어 둔다 — 서버가 받는 것이 그 넷이고 svg 는 «일부러» 빠졌다.
  //   늘리려면 서버 INLINE_SAFE_IMAGE_MIME 을 먼저 고치고 그다음 이 문구다.
  'contacts.edit.photo_bad_type': {
    ko: 'PNG · JPG · GIF · WEBP 만 쓸 수 있습니다',
    en: 'Only PNG, JPG, GIF and WEBP can be used',
    ja: 'PNG・JPG・GIF・WEBP のみ使えます',
  },
  // ★ 「명함은 담겼다」가 먼저 와야 한다 — 그 말이 없으면 사용자가 다시 「담기」를 눌러
  //   ALREADY_EXISTS 를 받고 까닭을 모른다. (새로 담을 때만 생긴다 · 2026-10-06 결정 ②)
  'contacts.edit.photo_saved_without': {
    ko: '명함은 담겼지만 이미지를 붙이지 못했습니다',
    en: 'The card was saved but the image could not be attached',
    ja: '名刺は保存されましたが画像を添付できませんでした',
  },
  // ★ 「올라갔는데 안 붙었다」는 «올리기 실패»와 다른 말이다 — 고아 파일이 남은 쪽이다.
  'contacts.edit.photo_attach_failed': {
    ko: '이미지는 올라갔지만 명함에 붙이지 못했습니다',
    en: 'The image uploaded but could not be attached to the card',
    ja: '画像はアップロードされましたが名刺に添付できませんでした',
  },
  'contacts.edit.note': { ko: '메모', en: 'Note', ja: 'メモ' },
  'contacts.edit.note_ph': { ko: '기억해 둘 것', en: 'Anything to remember', ja: '覚えておくこと' },
  'contacts.edit.where': { ko: '담을 곳', en: 'Save to', ja: '保存先' },
  'contacts.edit.star': { ko: '중요로 표시', en: 'Mark as starred', ja: '重要にする' },
  'contacts.edit.tags_ph': { ko: '치고 Enter', en: 'Type and press Enter', ja: '入力して Enter' },
  'contacts.edit.save': { ko: '담기', en: 'Save', ja: '保存' },
  'contacts.edit.update': { ko: '저장', en: 'Save', ja: '保存' },
  'contacts.edit.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'contacts.edit.close': { ko: '닫기', en: 'Close', ja: '閉じる' },
  'contacts.edit.email_required': { ko: '메일주소를 적어 주세요', en: 'Enter an email address', ja: 'メールアドレスを入力してください' },
  'contacts.edit.email_invalid': { ko: '메일주소 형식이 아닙니다', en: 'Not a valid email address', ja: 'メールアドレスの形式ではありません' },
  'contacts.edit.duplicate': { ko: '이미 담긴 주소입니다', en: 'Already in your contacts', ja: 'すでに保存済みのアドレスです' },
  // 2026-09-15 · #1374 — 공유에 이미 있는데 고칠 권한이 없을 때(409 editable:false).
  'contacts.edit.duplicate_shared': {
    ko: '이미 공유 명함첩에 있는 주소입니다. 고치려면 관리자에게 요청하세요',
    en: 'This address is already in the shared contacts. Ask an admin to change it.',
    ja: 'すでに共有アドレス帳にあるアドレスです。修正は管理者に依頼してください。',
  },
  'contacts.edit.failed': { ko: '담지 못했습니다. 잠시 뒤 다시 시도해 주세요', en: 'Could not save. Try again.', ja: '保存できませんでした。しばらくして再度お試しください。' },

  // ----- 줄 작업 -----
  'contacts.row.mail': { ko: '메일 보내기', en: 'Send mail', ja: 'メールを送る' },
  'contacts.row.edit': { ko: '수정', en: 'Edit', ja: '修正' },
  'contacts.row.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'contacts.row.star_on': { ko: '중요 해제', en: 'Remove star', ja: '重要を解除' },
  'contacts.row.star_off': { ko: '중요로 표시', en: 'Mark as starred', ja: '重要にする' },
  'contacts.delete.confirm': {
    ko: '이 연락처를 지울까요? 주고받은 메일은 그대로 남습니다.',
    en: 'Delete this contact? Your mail with them stays.',
    ja: 'この連絡先を削除しますか？やり取りしたメールはそのまま残ります。',
  },

  // ----- F3-2 사람 카드 -----
  'contacts.card.send': { ko: '메일 보내기', en: 'Send mail', ja: 'メールを送る' },
  'contacts.card.add': { ko: '명함첩에 추가', en: 'Add to contacts', ja: 'アドレス帳に追加' },
  'contacts.card.open': { ko: '연락처 열기', en: 'Open contact', ja: '連絡先を開く' },
  'contacts.card.block': { ko: '이 주소 차단', en: 'Block this address', ja: 'このアドレスを受信拒否' },
  'contacts.card.in_personal': { ko: '개인 명함첩', en: 'My contacts', ja: '個人アドレス帳' },
  'contacts.card.in_shared': { ko: '공유 명함첩', en: 'Shared contacts', ja: '共有アドレス帳' },
  'contacts.card.blocked': { ko: '차단됨', en: 'Blocked', ja: '受信拒否中' },
  'contacts.card.from_them': { ko: '이 사람이 보낸 메일', en: 'Mail from them', ja: 'この人からのメール' },
  'contacts.card.to_them': { ko: '내가 보낸 메일', en: 'Mail I sent them', ja: '自分が送ったメール' },
  'contacts.card.aria': { ko: '{name} 정보', en: 'About {name}', ja: '{name} の情報' },

  // 차단 결과 — 「됐다」와 「이미 그렇다」는 함께 묶고, 손댈 곳이 있는 둘만 가른다.
  'contacts.block.done': { ko: '앞으로 이 주소의 메일은 스팸함으로 갑니다', en: 'Future mail from this address goes to spam', ja: '今後このアドレスのメールは迷惑メールに入ります' },
  'contacts.block.already': { ko: '이미 차단된 주소입니다', en: 'Already blocked', ja: 'すでに受信拒否中です' },
  'contacts.block.in_allow': {
    ko: '수신허용목록에 있는 주소입니다. 환경설정에서 먼저 지워 주세요',
    en: 'This address is on your allow list. Remove it in settings first.',
    ja: '受信許可リストにあるアドレスです。環境設定で先に削除してください。',
  },
  'contacts.block.self': { ko: '내 주소는 차단할 수 없습니다', en: 'You cannot block your own address', ja: '自分のアドレスは受信拒否できません' },
  'contacts.block.failed': { ko: '차단하지 못했습니다', en: 'Could not block', ja: '受信拒否できませんでした' },

  // ----- E1-4 골라 담기 -----
  'contacts.pick.title': { ko: '명함첩에 담기', en: 'Add to contacts', ja: 'アドレス帳に追加' },
  'contacts.pick.open': { ko: '명함첩에 담기', en: 'Add to contacts', ja: 'アドレス帳に追加' },
  'contacts.pick.count': { ko: '이 메일에 있는 주소 {n}개', en: '{n} addresses in this mail', ja: 'このメールのアドレス {n} 件' },
  'contacts.pick.selected': { ko: '{n}명 선택', en: '{n} selected', ja: '{n} 名選択' },
  'contacts.pick.submit': { ko: '{n}명 담기', en: 'Add {n}', ja: '{n} 名を追加' },
  'contacts.pick.col_mobile': { ko: '휴대폰', en: 'Mobile', ja: '携帯' },
  'contacts.pick.me': { ko: '나 — 담지 않음', en: 'You — not added', ja: '自分 — 追加しません' },
  'contacts.pick.already': { ko: '이미 담김', en: 'Already saved', ja: '保存済み' },
  'contacts.pick.name_ph': { ko: '(이름없음)', en: '(No name)', ja: '(名前なし)' },
  'contacts.pick.mobile_ph': { ko: '010-0000-0000', en: '010-0000-0000', ja: '010-0000-0000' },
  'contacts.pick.tag_ph': { ko: '태그 고르기', en: 'Pick tags', ja: 'タグを選ぶ' },
  'contacts.pick.hint': {
    ko: '이름을 비워 두면 「(이름없음)」으로 담깁니다. 나중에 목록에서 채울 수 있습니다.',
    en: 'Leave the name blank and it is saved as “(No name)”. You can fill it in later.',
    ja: '名前を空にすると「(名前なし)」で保存されます。あとから入力できます。',
  },
  'contacts.pick.result': { ko: '{added}명 담았습니다', en: 'Added {added}', ja: '{added} 名を追加しました' },
  'contacts.pick.select_all': { ko: '전체 선택', en: 'Select all', ja: 'すべて選択' },

  // 메일 상세 주소 줄
  'contacts.addr.add': { ko: '명함첩에 담기', en: 'Add to contacts', ja: 'アドレス帳に追加' },
  'contacts.addr.in_book': { ko: '명함첩', en: 'In contacts', ja: 'アドレス帳' },
};

export type ContactsI18nKey = keyof typeof MESSAGES;

export function tContacts(key: ContactsI18nKey): string {
  const m = MESSAGES[key];
  if (!m) return key;
  return m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
}

/** `{n}` 같은 자리를 채운다 — 문장을 조각내 이어 붙이면 어순이 다른 말에서 깨진다. */
export function tContactsWith(key: ContactsI18nKey, vars: Record<string, string | number>): string {
  let out = tContacts(key);
  for (const [k, v] of Object.entries(vars)) out = out.replace(`{${k}}`, String(v));
  return out;
}

export function getContactsLang(): Lang { return getSharedLang(); }
export function setContactsLang(value: Lang): void { setSharedLang(value); }
export function subscribeContactsLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
